import { useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useActionDock, useOnboardingV2 } from './onboardingV2';
import styles from './StepLayout.module.css';

/**
 * Shared step-body atoms used across Steps 1–6. Step 7 Welcome owns
 * its own layout and does NOT use these (it's bare-shell).
 *
 * Pattern across each step:
 *   <StepHeading>What brings you to running?</StepHeading>
 *   <StepSub>This shapes everything…</StepSub>
 *   <StepStack>{ ...option rows / inputs / chips }</StepStack>
 *   <StepCta onClick={onContinue} disabled={!canContinue}>Continue</StepCta>
 *   <TertiaryLink onClick={skip}>Skip…</TertiaryLink>
 */

export function StepHeading({ children, id }: { children: ReactNode; id?: string }) {
  // The OnboardingShell already focuses its own ambient-panel <h1>
  // (the step name in serif), so this is rendered as <h2> for proper
  // document outline.
  return (
    <h2 className={styles.h2} id={id}>
      {children}
    </h2>
  );
}

export function StepSub({ children }: { children: ReactNode }) {
  return <p className={styles.sub}>{children}</p>;
}

export function StepStack({
  children,
  gap,
}: {
  children: ReactNode;
  /** Override the default 10px gap. */
  gap?: number;
}) {
  return (
    <div className={styles.stack} style={gap !== undefined ? { gap } : undefined}>
      {children}
    </div>
  );
}

type StepCtaProps = {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Optional aria-label override. */
  'aria-label'?: string;
  /**
   * Part 7, ruling 11 — a Continue that is `aria-disabled` but not `disabled` is
   * WAITING: still pressable (the press names what is wrong), so under the restyle
   * it wears the outlined marine look instead of the flat disabled one. The steps
   * that refuse on press already pass this; nothing new is asked of them.
   */
  'aria-disabled'?: boolean | 'true' | 'false';
};

/**
 * Where the press of a waiting Continue lands: the field that is refusing, so a
 * screen reader hears it and its sentence (ruling 3, P-free). Looks for the box
 * marked invalid, else for the box a live sentence describes. Runs after the press
 * has rendered the sentence.
 */
function focusRefusingField(): void {
  if (typeof document === 'undefined') return;
  window.setTimeout(() => {
    const scope = document.querySelector('main') ?? document;
    const invalid = scope.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (invalid) {
      invalid.focus();
      return;
    }
    const alert = scope.querySelector<HTMLElement>('[role="alert"][id]');
    if (!alert) return;
    scope.querySelector<HTMLElement>(`[aria-describedby~="${alert.id}"]`)?.focus();
  }, 0);
}

/**
 * Primary marine CTA used at the bottom of each step. Full-width,
 * pill-shaped. Disabled state uses ink-200/ink-400 (not the marine
 * color) so it reads as inactive.
 *
 * Under Part 7 (flag `onboarding_2_0_v1`) it has three looks (ready, waiting,
 * disabled) and, on a phone, it is portaled into the shell's docked action bar.
 */
export function StepCta({ children, onClick, disabled, ...rest }: StepCtaProps) {
  const v2 = useOnboardingV2();
  const dock = useActionDock();
  const waiting = !disabled && (rest['aria-disabled'] === true || rest['aria-disabled'] === 'true');
  const handleClick = useCallback(() => {
    onClick();
    if (waiting) focusRefusingField();
  }, [onClick, waiting]);

  if (!v2) {
    return (
      <button
        type="button"
        className={styles.cta}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        {children}
      </button>
    );
  }

  const look = disabled ? styles.ctaOff : waiting ? styles.ctaWait : styles.ctaReady;
  const button = (
    <button
      type="button"
      className={`${styles.cta} ${look}`}
      onClick={handleClick}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  );
  if (!dock) return button;
  // The bar's slot mounts with the shell; until it has, the button is simply absent
  // (never drawn in the page and then moved).
  return dock.ctaEl ? createPortal(button, dock.ctaEl) : null;
}

/**
 * Tertiary skip / escape-hatch link rendered below the primary CTA.
 * Centered, ink-500, hovers to ink-700.
 */
export function TertiaryLink({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" className={styles.tertiary} onClick={onClick}>
      {children}
    </button>
  );
}
